import React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useIsMobile } from "~/hooks/use-mobile";
import { cn } from "~/lib/utils";

// // // //

/**
 * Component that displays a description for an individual
 * page (network, platform, asset, etc.)with truncation and expand/collapse functionality
 */
export function DescriptionDisplay(props: {
    description: string | null;
    className?: string;
    textClassName?: string;
    toggleVariant?: "inline" | "pill";
}) {
    const {
        description,
        className,
        textClassName,
        toggleVariant = "inline",
    } = props;
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [pillOverflows, setPillOverflows] = React.useState(
        Boolean(description && description.length > 400),
    );
    const descriptionRef = React.useRef<HTMLParagraphElement>(null);
    const isMobile = useIsMobile();
    const reduceMotion = useReducedMotion();
    const usePillToggle = toggleVariant === "pill";
    const threshold = isMobile ? 150 : 400;
    const lengthShouldTruncate = Boolean(
        description && description.length > threshold,
    );
    const shouldTruncate = usePillToggle ? pillOverflows : lengthShouldTruncate;

    React.useEffect(() => {
        if (!usePillToggle || !description || !descriptionRef.current) {
            setPillOverflows(false);
            return;
        }

        const descriptionElement = descriptionRef.current;
        const updateOverflow = () => {
            const lineHeight = Number.parseFloat(
                window.getComputedStyle(descriptionElement).lineHeight,
            );
            const collapsedHeight = lineHeight * 5;

            setPillOverflows(
                Number.isFinite(collapsedHeight) &&
                    descriptionElement.scrollHeight > collapsedHeight + 1,
            );
        };

        updateOverflow();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", updateOverflow);
            return () => window.removeEventListener("resize", updateOverflow);
        }

        const resizeObserver = new ResizeObserver(updateOverflow);
        resizeObserver.observe(descriptionElement);

        return () => resizeObserver.disconnect();
    }, [description, usePillToggle]);

    if (!description) {
        return null;
    }

    const displayDescription =
        shouldTruncate && !isExpanded && !usePillToggle
            ? description.slice(0, threshold) + "..."
            : description;

    const handleToggle = () => {
        setIsExpanded((currentValue) => !currentValue);
    };

    if (!usePillToggle) {
        return (
            <div className={cn("mt-3 mb-6", className)}>
                <p
                    className={cn(
                        "font-light text-sm text-slate-600 max-w-4xl",
                        textClassName,
                    )}
                >
                    {displayDescription}
                    {shouldTruncate && (
                        <button
                            onClick={handleToggle}
                            className="ml-2 text-sm text-link hover:text-link-hover font-medium transition-colors"
                            aria-label={
                                isExpanded
                                    ? "Show less description"
                                    : "Show more description"
                            }
                        >
                            {isExpanded ? "View less" : "Read more"}
                        </button>
                    )}
                </p>
            </div>
        );
    }

    return (
        <div className={cn("mt-3 mb-6", className)}>
            <motion.div
                initial={false}
                animate={{
                    height: shouldTruncate && !isExpanded ? "5lh" : "auto",
                }}
                transition={{
                    duration: reduceMotion ? 0 : 0.4,
                    ease: [0.22, 1, 0.36, 1],
                }}
                className={cn("overflow-hidden", {
                    relative: shouldTruncate,
                })}
            >
                <p
                    ref={descriptionRef}
                    className={cn(
                        "font-light text-sm text-slate-600 max-w-4xl",
                        textClassName,
                    )}
                >
                    {displayDescription}
                </p>

                <AnimatePresence initial={false}>
                    {shouldTruncate && !isExpanded && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{
                                duration: reduceMotion ? 0 : 0.2,
                            }}
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-linear-to-b from-transparent via-background-02/80 to-background-02"
                        />
                    )}
                </AnimatePresence>
            </motion.div>

            {shouldTruncate && (
                <div
                    className={cn(
                        "relative flex justify-center transition-[margin] duration-300 ease-out motion-reduce:transition-none",
                        {
                            "-mt-2": !isExpanded,
                            "mt-2": isExpanded,
                        },
                    )}
                >
                    <motion.button
                        onClick={handleToggle}
                        whileTap={reduceMotion ? undefined : { scale: 0.97 }}
                        className="relative inline-flex items-center justify-center rounded-full bg-card px-3 py-1.5 text-xs font-medium text-foreground ring-1 ring-foreground/10 hover:bg-background-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        aria-expanded={isExpanded}
                        aria-label={
                            isExpanded
                                ? "Show less description"
                                : "Show more description"
                        }
                    >
                        <span
                            className="absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2 pointer-fine:hidden"
                            aria-hidden="true"
                        />
                        {isExpanded ? "View less" : "Read more"}
                    </motion.button>
                </div>
            )}
        </div>
    );
}
